import { SponsoredLabel } from '../../features/ads/SponsoredContent';
import SponsoredExposure from '../../features/ads/SponsoredExposure';
import { adsApi } from '../../api/ads';
import RecoverableImage from '../../features/images/RecoverableImage';
import React from 'react';
import { Link } from 'react-router-dom';
import { useWebLocale } from '../../contexts/WebLocaleContext';
import { albumPath } from './albumPath';
import AlbumContentTitle from './AlbumContentTitle';
import PlaylistArtwork from './PlaylistArtwork';
import HydratedPlaylistArtwork from './HydratedPlaylistArtwork';
import AppAuthor from '../../app-ui/AppAuthor';
import baseline, { appTextStyle, playlistText } from '../../app-ui/baseline';
import './ExploreFeatureCard.css';

export function playlistFeatureMetadata(item, locale) {
  const tracks = item.type === 'TRACK';
  const count = (tracks ? item.trackCount : item.albumCount) ?? 0;
  const label = playlistText(locale, tracks ? 'trackItems' : 'albumItems', { count });
  if (!tracks || !(item.totalDurationMs > 0)) return label;
  const total = Math.max(1, Math.round(item.totalDurationMs / 60000));
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  const duration =
    locale === 'en'
      ? [hours && `${hours} hr`, minutes && `${minutes} min`].filter(Boolean).join(' ')
      : [hours && `${hours}시간`, minutes && `${minutes}분`].filter(Boolean).join(' ');
  return `${label} · ${duration}`;
}

function AmbientArtwork({ image }) {
  return (
    <span className="pp-feature-ambient" aria-hidden="true">
      {image && <RecoverableImage src={image} alt="" loading="lazy" />}
    </span>
  );
}

// Submitted iOS60: recommendation copy left, playlist artwork left; 182/150 panel/cover.
export function RecommendationFeatureCard({ item, campaign }) {
  const { locale } = useWebLocale();
  const styles = baseline.features.recommendation;
  const note = (
    locale === 'en'
      ? item.recommendationCommentEn || item.recommendationComment
      : item.recommendationComment
  )?.trim();
  const content = (
    <>
      <AmbientArtwork image={item.albumImageUrl} />
      <div className="pp-app-feature-copy">
        <AlbumContentTitle
          item={item}
          style={appTextStyle(baseline.tokens.typography.screenTitle, styles.albumTitle)}
          inlineExplicit
          title={item.albumTitle}
        />
        <span
          className="pp-app-feature-artist"
          style={appTextStyle(styles.artistName, baseline.contentText.artist)}
        >
          {item.artist?.name ||
            item.albumArtistName ||
            (locale === 'en' ? 'Unknown artist' : '아티스트 정보 없음')}
        </span>
        <span className="pp-app-feature-rule" aria-hidden="true" />
        {note && (
          <p
            className="pp-app-feature-note"
            style={appTextStyle(styles.noteText, baseline.contentText.metadata)}
          >
            {note}
          </p>
        )}
        {campaign ? (
          <div className="pp-feature-ad-control">
            <SponsoredLabel campaign={campaign} placementKey="EXPLORE_RECOMMENDED" compact />
          </div>
        ) : item.isAdMarked ? (
          <span className="pp-app-feature-ad pp-sponsored-label pp-sponsored-compact">
            {locale === 'en' ? 'Ad' : '광고'}
          </span>
        ) : null}
      </div>
      <span className="pp-app-feature-cover">
        {item.albumImageUrl ? (
          <RecoverableImage src={item.albumImageUrl} alt="" loading="lazy" />
        ) : (
          <span aria-hidden="true">P</span>
        )}
      </span>
    </>
  );
  if (!campaign)
    return (
      <Link className="pp-app-feature pp-app-recommendation" to={albumPath(item)}>
        {content}
      </Link>
    );
  return (
    <SponsoredExposure
      campaignId={campaign.id}
      placementKey="EXPLORE_RECOMMENDED"
      className="pp-app-feature pp-app-recommendation"
    >
      <Link
        className="pp-feature-primary-link"
        to={albumPath(item)}
        aria-label={`${item.albumTitle} · ${item.artist?.name || ''}`}
        onClick={() => {
          void adsApi.click(campaign.id, 'EXPLORE_RECOMMENDED').catch(() => {});
        }}
      />
      {content}
    </SponsoredExposure>
  );
}

export function PlaylistFeatureCard({ item }) {
  const { locale } = useWebLocale();
  const styles = baseline.features.playlist;
  return (
    <Link
      className="pp-app-feature pp-app-playlist"
      to={`/lists/${encodeURIComponent(item.slug || item.id)}`}
    >
      <HydratedPlaylistArtwork playlist={item} canonical>
        {(artwork, detail) => (
          <>
            <AmbientArtwork image={artwork.imageUrl || artwork.previewImageUrls?.find(Boolean)} />
            <span className="pp-app-feature-cover">
              <PlaylistArtwork playlist={artwork} />
            </span>
            <div className="pp-app-feature-copy">
              <strong className="pp-app-feature-title" style={appTextStyle(styles.title)}>
                {item.title}
              </strong>
              <AppAuthor
                name={item.username || item.userNickname || 'postpop'}
                image={item.userProfileImageUrl}
                mark={item.userProfileMark}
              />
              <p
                className="pp-app-feature-metadata"
                style={appTextStyle(styles.metadata, baseline.contentText.metadata)}
              >
                {playlistFeatureMetadata(
                  {
                    ...item,
                    totalDurationMs:
                      item.totalDurationMs ??
                      detail?.tracks?.reduce((total, track) => total + (track.durationMs ?? 0), 0),
                  },
                  locale,
                )}
              </p>
            </div>
          </>
        )}
      </HydratedPlaylistArtwork>
    </Link>
  );
}
